import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Compass, ArrowRight, Layers, Loader2, ChevronRight } from "lucide-react";

// Grid is [-100..100] × [-100..100] in data space. We render to a 720×720 SVG with 60px padding.
const SVG_SIZE = 720;
const PAD = 60;
const INNER = SVG_SIZE - 2 * PAD; // 600

// Map data coord [-100..100] → SVG coord [PAD..SVG_SIZE-PAD]
const toX = (x) => PAD + ((x + 100) / 200) * INNER;
const toY = (y) => PAD + ((100 - y) / 200) * INNER; // y flipped so +Y is up

function ArchetypeZone({ arch }) {
  // Each archetype zone is a coloured tinted hexagonal-ish ellipse.
  const cx = toX(arch.coord[0]);
  const cy = toY(arch.coord[1]);
  return (
    <g opacity={0.18} data-testid={`archetype-zone-${arch.name}`}>
      <ellipse cx={cx} cy={cy} rx={130} ry={110} fill={arch.color} />
    </g>
  );
}

function ArchetypeLabel({ arch }) {
  const cx = toX(arch.coord[0]);
  const cy = toY(arch.coord[1]);
  return (
    <g data-testid={`archetype-label-${arch.name}`}>
      <text x={cx} y={cy - 14} textAnchor="middle" fontFamily="JetBrains Mono, monospace"
        fontSize={12} fontWeight={700} fill={arch.color} letterSpacing={1.5}>
        {arch.label}
      </text>
      <text x={cx} y={cy + 4} textAnchor="middle" fontFamily="JetBrains Mono, monospace"
        fontSize={9} fill="var(--bm-text-muted)" letterSpacing={0.5}>
        {arch.subtitle}
      </text>
    </g>
  );
}

function AxisFrame() {
  // Vertical axis label + horizontal axis label.
  const mid = SVG_SIZE / 2;
  return (
    <g>
      {/* Grid lines */}
      <line x1={mid} y1={PAD} x2={mid} y2={SVG_SIZE - PAD} stroke="var(--bm-border)" strokeWidth={1} strokeDasharray="2 4" />
      <line x1={PAD} y1={mid} x2={SVG_SIZE - PAD} y2={mid} stroke="var(--bm-border)" strokeWidth={1} strokeDasharray="2 4" />
      {/* Outer frame */}
      <rect x={PAD} y={PAD} width={INNER} height={INNER} fill="none" stroke="var(--bm-border)" strokeWidth={1} />

      {/* Axis labels — anchored just inside the frame */}
      <text x={SVG_SIZE - PAD - 6} y={mid - 6} textAnchor="end" fontFamily="JetBrains Mono, monospace"
        fontSize={10} fill="var(--bm-text-dim)" letterSpacing={1}>
        EXTROVERT →
      </text>
      <text x={PAD + 6} y={mid - 6} textAnchor="start" fontFamily="JetBrains Mono, monospace"
        fontSize={10} fill="var(--bm-text-dim)" letterSpacing={1}>
        ← INTROVERT
      </text>
      <text x={mid + 6} y={PAD + 14} textAnchor="start" fontFamily="JetBrains Mono, monospace"
        fontSize={10} fill="var(--bm-text-dim)" letterSpacing={1}>
        ↑ CONSIDERED
      </text>
      <text x={mid + 6} y={SVG_SIZE - PAD - 6} textAnchor="start" fontFamily="JetBrains Mono, monospace"
        fontSize={10} fill="var(--bm-text-dim)" letterSpacing={1}>
        ↓ PLAYFUL
      </text>
    </g>
  );
}

function BriefMarker({ b, project, activeSlug }) {
  if (!b?.coord) return null;
  const bx = toX(b.coord[0]);
  const by = toY(b.coord[1]);
  // Color: if linked to a project, use project color; else neutral
  const color = project?.project_color || "#7aa2c8";
  const dimmed = activeSlug && project?.project_slug !== activeSlug;
  const px = project ? toX(project.recommended.coord[0]) : null;
  const py = project ? toY(project.recommended.coord[1]) : null;

  return (
    <g data-testid={`brief-marker-${b.brief_id}`} opacity={dimmed ? 0.18 : 0.92}>
      {/* Hairline link to parent project (recommended pos) */}
      {px !== null && py !== null && (
        <line x1={px} y1={py} x2={bx} y2={by} stroke={color} strokeWidth={0.75} opacity={0.35} />
      )}
      {/* Diamond marker */}
      <g transform={`translate(${bx}, ${by}) rotate(45)`}>
        <rect x={-5} y={-5} width={10} height={10} fill={color} opacity={0.92} stroke="rgba(0,0,0,0.45)" strokeWidth={0.75} />
      </g>
    </g>
  );
}

function ProjectMarker({ p, isActive, onClick }) {
  const cx = toX(p.current.coord[0]);
  const cy = toY(p.current.coord[1]);
  const rx = toX(p.recommended.coord[0]);
  const ry = toY(p.recommended.coord[1]);
  const color = p.project_color || "#F2C94C";

  return (
    <g
      data-testid={`project-marker-${p.project_slug}`}
      onClick={onClick}
      style={{ cursor: "pointer" }}
      opacity={isActive === null || isActive ? 1 : 0.25}
    >
      {/* Trajectory arrow */}
      <defs>
        <marker id={`arrow-${p.project_slug}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill={color} opacity={0.9} />
        </marker>
      </defs>
      <line
        x1={cx} y1={cy} x2={rx} y2={ry}
        stroke={color}
        strokeWidth={isActive ? 2.5 : 1.5}
        strokeDasharray="4 3"
        opacity={isActive === null || isActive ? 0.85 : 0.3}
        markerEnd={`url(#arrow-${p.project_slug})`}
      />

      {/* CURRENT — hollow ring */}
      <circle cx={cx} cy={cy} r={isActive ? 11 : 8} fill="none" stroke={color}
        strokeWidth={2} opacity={0.9} />
      <circle cx={cx} cy={cy} r={3} fill={color} opacity={0.9} />

      {/* RECOMMENDED — filled disc */}
      <circle cx={rx} cy={ry} r={isActive ? 13 : 10} fill={color} opacity={0.95} />
      <circle cx={rx} cy={ry} r={isActive ? 16 : 0} fill="none" stroke={color} opacity={0.4} />

      {/* Label near recommended */}
      <text
        x={rx + 14}
        y={ry + 4}
        fontFamily="Space Grotesk, sans-serif"
        fontSize={isActive ? 14 : 12}
        fontWeight={700}
        fill="var(--bm-text)"
        data-testid={`project-label-${p.project_slug}`}
      >
        {p.project_name}
      </text>
    </g>
  );
}

function CompetitiveDot({ neighbour, color }) {
  const x = toX(neighbour.coord[0]);
  const y = toY(neighbour.coord[1]);
  return (
    <g>
      <circle cx={x} cy={y} r={4} fill="var(--bm-border-strong)" stroke={color || "var(--bm-text)"} strokeWidth={1} />
      <text x={x + 8} y={y + 3} fontFamily="JetBrains Mono, monospace" fontSize={9} fill="var(--bm-text-muted)">
        {neighbour.name}
      </text>
    </g>
  );
}

function ArchetypeVotesBar({ votes }) {
  const total = Object.values(votes).reduce((a, b) => a + b, 0) || 1;
  const ARCH_COLORS = {
    BOLD: "#E04848", CAREFREE: "#F2C94C", CARING: "#E55B8D",
    ASPIRATIONAL: "#A270D6", TRUSTED: "#4FB6E8", DISCERNING: "#5BC988",
  };
  return (
    <div className="space-y-1.5">
      {Object.entries(votes).map(([name, v]) => {
        const pct = Math.round((v / total) * 100);
        return (
          <div key={name} className="flex items-center gap-2 text-xs font-mono">
            <span className="w-24 text-bm-dim">{name}</span>
            <div className="flex-1 h-1.5 bg-white/5 relative">
              <div className="h-full" style={{ width: `${pct}%`, background: ARCH_COLORS[name] || "#fff" }}></div>
            </div>
            <span className="w-8 text-right text-bm-muted">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function EvidenceItem({ ev }) {
  const KIND_COLORS = {
    signal: "border-bm-cyan/40 text-bm-cyan",
    insight: "border-bm-amber/40 text-bm-amber",
    opportunity: "border-bm-green/40 text-bm-green",
  };
  return (
    <div className="border-l-2 border-white/10 pl-3 py-1.5">
      <div className="flex items-center gap-2 mb-1">
        <span className={`label-mono px-1 py-0.5 border ${KIND_COLORS[ev.kind] || "border-white/10"}`}>{ev.kind.toUpperCase()}</span>
        {ev.conviction !== undefined && ev.conviction !== null && (
          <span className="label-mono text-bm-green">{ev.conviction}%</span>
        )}
        {ev.ux_label && <span className="label-mono text-bm-dim">{ev.ux_label}</span>}
        <span className="label-mono text-bm-dim ml-auto">w {ev.weight}</span>
      </div>
      <div className="text-xs text-white/90 leading-snug">{ev.title}</div>
      <div className="flex flex-wrap gap-1 mt-1">
        {(ev.archetypes || []).map((a) => (
          <span key={a} className="text-[9px] font-mono text-bm-dim border border-white/10 px-1">
            {a}
          </span>
        ))}
      </div>
    </div>
  );
}

function ProjectPanel({ p }) {
  if (!p) return null;
  const dx = p.recommended.coord[0] - p.current.coord[0];
  const dy = p.recommended.coord[1] - p.current.coord[1];
  return (
    <div className="space-y-4" data-testid={`needscope-panel-${p.project_slug}`}>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3" style={{ background: p.project_color || "#F2C94C" }}></div>
          <h2 className="font-heading text-2xl font-black tracking-tight" data-testid="panel-project-name">{p.project_name}</h2>
        </div>
        <p className="text-sm text-bm-muted italic leading-snug">{p.dna_phrase}</p>
      </div>

      {/* Current → Recommended */}
      <div className="bm-card-elev p-4 space-y-3">
        <div className="flex items-stretch gap-3">
          <div className="flex-1">
            <div className="label-mono text-bm-dim">CURRENT</div>
            <div className="font-heading text-xl font-bold mt-0.5" data-testid="panel-current-archetype">{p.current.archetype}</div>
            <div className="font-mono text-xs text-bm-muted">{p.current.coord.join(", ")}</div>
          </div>
          <ArrowRight className="w-5 h-5 text-bm-amber self-center shrink-0" />
          <div className="flex-1 text-right">
            <div className="label-mono text-bm-amber">RECOMMENDED</div>
            <div className="font-heading text-xl font-bold mt-0.5" data-testid="panel-recommended-archetype">{p.recommended.archetype}</div>
            <div className="font-mono text-xs text-bm-muted">{p.recommended.coord.join(", ")}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/5 text-center">
          <div>
            <div className="label-mono text-bm-dim">DISTANCE</div>
            <div className={`font-mono text-sm mt-0.5 ${p.trajectory.distance >= 28 ? "text-bm-danger" : p.trajectory.distance >= 12 ? "text-bm-amber" : "text-bm-green"}`}>
              {p.trajectory.distance}
            </div>
          </div>
          <div>
            <div className="label-mono text-bm-dim">DIRECTION</div>
            <div className="font-mono text-[11px] mt-0.5 leading-tight text-white">{p.trajectory.direction_hint}</div>
          </div>
          <div>
            <div className="label-mono text-bm-dim">CONFIDENCE</div>
            <div className={`font-mono text-sm mt-0.5 ${p.confidence >= 70 ? "text-bm-green" : "text-bm-amber"}`}>{p.confidence}%</div>
          </div>
        </div>

        <div className="pt-2 border-t border-white/5 text-xs font-mono leading-relaxed" data-testid="panel-verdict">
          <span className="text-bm-dim">VERDICT · </span>
          <span className={p.trajectory.distance >= 28 ? "text-bm-danger" : p.trajectory.distance >= 12 ? "text-bm-amber" : "text-bm-green"}>
            {p.trajectory.verdict}
          </span>
        </div>

        <div className="text-[10px] font-mono text-bm-dim">
          δx {dx >= 0 ? "+" : ""}{dx.toFixed(1)} (expressive) · δy {dy >= 0 ? "+" : ""}{dy.toFixed(1)} (considered)
        </div>
      </div>

      {/* Archetype vote split */}
      <div className="bm-card p-4">
        <div className="label-mono text-bm-dim mb-3 flex items-center gap-1.5">
          <Layers className="w-3 h-3" />ARCHETYPE PULL · {p.evidence_count} ITEMS
        </div>
        <ArchetypeVotesBar votes={p.recommended.archetype_votes} />
      </div>

      {/* Competitive neighbours */}
      {p.competitive_neighbours?.length > 0 && (
        <div className="bm-card p-4">
          <div className="label-mono text-bm-dim mb-2">COMPETITIVE NEIGHBOURS</div>
          <div className="space-y-2">
            {p.competitive_neighbours.map((n) => (
              <div key={n.name} className="flex items-center gap-2 text-xs">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--bm-text-dim)" }}></div>
                <span className="text-white font-bold">{n.name}</span>
                <span className="text-bm-muted">· {n.market}</span>
                <span className="text-bm-dim ml-auto label-mono">{n.archetype}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top evidence */}
      <div className="bm-card p-4">
        <div className="label-mono text-bm-dim mb-3 flex items-center gap-1.5">
          <ChevronRight className="w-3 h-3" />TOP EVIDENCE
        </div>
        <div className="space-y-2">
          {p.evidence.slice(0, 8).map((ev) => (
            <EvidenceItem key={`${ev.kind}-${ev.id}`} ev={ev} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function NeedScope() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(null);
  const [showBriefs, setShowBriefs] = useState(true);

  useEffect(() => {
    api.needscope().then((d) => {
      setData(d);
      if (d.projects?.length) setActiveSlug(d.projects[0].project_slug);
      setLoading(false);
    });
    // Fire-once on mount — api + setters are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeProject = useMemo(() => {
    if (!data || !activeSlug) return null;
    return data.projects.find((p) => p.project_slug === activeSlug);
  }, [data, activeSlug]);

  const activeBriefs = useMemo(() => {
    if (!data || !activeSlug) return [];
    return (data.briefs || []).filter((b) => b.linked_project_slug === activeSlug);
  }, [data, activeSlug]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-bm-cyan font-mono text-xs scanline p-6">
        <Loader2 className="w-4 h-4 animate-spin" />LOADING NEEDSCOPE GRID...
      </div>
    );
  }

  if (!data || !data.projects?.length) {
    return <div className="font-mono text-sm text-bm-muted p-6">No projects to plot.</div>;
  }

  const projectsBySlug = Object.fromEntries(data.projects.map((p) => [p.project_slug, p]));

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono text-bm-amber flex items-center gap-2">
            <Compass className="w-3.5 h-3.5" />BRAND POSITIONING · NEEDSCOPE
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            Where each brand should sit.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-3xl">
            Hand-seeded <span className="text-white">current</span> position vs. <span className="text-bm-amber">data-driven recommended</span> position
            for each Meolaa project. Diamonds = active <span className="text-bm-cyan">Briefs</span> plotted by their claims + sensory targets,
            connected by hairline to their parent project — drift between the two = positioning gap inside the pipeline.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs font-mono text-bm-muted cursor-pointer" data-testid="toggle-briefs">
            <input
              type="checkbox"
              checked={showBriefs}
              onChange={(e) => setShowBriefs(e.target.checked)}
              className="accent-bm-amber w-3.5 h-3.5"
            />
            SHOW BRIEFS · {data.briefs?.length || 0}
          </label>
          <div className="flex flex-wrap gap-1.5" data-testid="project-tabs">
            {data.projects.map((p) => (
              <button
                key={p.project_slug}
                onClick={() => setActiveSlug(p.project_slug)}
                data-testid={`project-tab-${p.project_slug}`}
                className={`px-2.5 py-1 text-xs font-mono border transition-colors ${
                  activeSlug === p.project_slug
                    ? "bg-bm-amber text-black border-bm-amber"
                    : "text-bm-muted border-white/15 hover:border-bm-amber/50 hover:text-white"
                }`}
              >
                {p.project_name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
        {/* SVG grid */}
        <div className="bm-card p-3 overflow-auto" data-testid="needscope-grid">
          <svg
            viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
            width="100%"
            className="w-full h-auto aspect-square"
            style={{ background: "var(--bm-surface)", display: "block" }}
          >
            {data.archetypes.map((a) => <ArchetypeZone key={a.name} arch={a} />)}
            <AxisFrame />
            {data.archetypes.map((a) => <ArchetypeLabel key={`l-${a.name}`} arch={a} />)}

            {/* Briefs (diamonds) — rendered behind project markers so projects pop */}
            {showBriefs && (data.briefs || []).map((b) => (
              <BriefMarker key={b.brief_id} b={b} project={projectsBySlug[b.linked_project_slug]} activeSlug={activeSlug} />
            ))}

            {/* Competitive neighbours of the active project */}
            {activeProject?.competitive_neighbours?.map((n) => (
              <CompetitiveDot key={`${activeProject.project_slug}-${n.name}`}
                neighbour={n} color={activeProject.project_color} />
            ))}

            {data.projects.map((p) => (
              <ProjectMarker
                key={p.project_slug}
                p={p}
                isActive={activeSlug ? p.project_slug === activeSlug : null}
                onClick={() => setActiveSlug(p.project_slug)}
              />
            ))}
          </svg>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-2 px-2 py-2 border-t border-white/5 text-[11px] font-mono text-bm-muted">
            <div className="flex items-center gap-1.5">
              <svg width="22" height="14"><circle cx={7} cy={7} r={5} fill="none" stroke="#F2C94C" strokeWidth={2} /></svg>
              <span>CURRENT</span>
            </div>
            <div className="flex items-center gap-1.5">
              <svg width="22" height="14"><circle cx={7} cy={7} r={5} fill="#F2C94C" /></svg>
              <span>RECOMMENDED</span>
            </div>
            <div className="flex items-center gap-1.5">
              <svg width="32" height="14"><line x1={2} y1={7} x2={28} y2={7} stroke="#F2C94C" strokeWidth={1.5} strokeDasharray="4 3" /></svg>
              <span>TRAJECTORY</span>
            </div>
            <div className="flex items-center gap-1.5">
              <svg width="22" height="14"><rect x={2} y={3} width={8} height={8} fill="#F2C94C" transform="rotate(45 6 7)" /></svg>
              <span>BRIEF</span>
            </div>
            <div className="flex items-center gap-1.5">
              <svg width="22" height="14"><circle cx={7} cy={7} r={3.5} fill="var(--bm-border-strong)" stroke="var(--bm-text)" /></svg>
              <span>COMPETITIVE REF</span>
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <ProjectPanel p={activeProject} />

          {/* Active project's briefs */}
          {activeBriefs.length > 0 && (
            <div className="bm-card p-4" data-testid={`briefs-panel-${activeSlug}`}>
              <div className="label-mono text-bm-dim mb-3 flex items-center justify-between">
                <span>BRIEFS IN PIPELINE · {activeBriefs.length}</span>
              </div>
              <div className="space-y-2">
                {activeBriefs.map((b) => {
                  const proj = projectsBySlug[b.linked_project_slug];
                  // Drift = distance between brief coord and project recommended coord
                  const px = proj?.recommended?.coord || [0, 0];
                  const dx = b.coord[0] - px[0];
                  const dy = b.coord[1] - px[1];
                  const drift = Math.round(Math.sqrt(dx * dx + dy * dy) * 10) / 10;
                  return (
                    <div key={b.brief_id} className="border-l-2 border-bm-amber/40 pl-3 py-1.5" data-testid={`brief-row-${b.brief_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-white font-bold truncate">{b.title}</div>
                        <span className={`label-mono px-1 py-0.5 border shrink-0 ${
                          drift < 15 ? "border-bm-green/30 text-bm-green" : drift < 30 ? "border-bm-amber/30 text-bm-amber" : "border-bm-danger/30 text-bm-danger"
                        }`}>
                          DRIFT {drift}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] font-mono text-bm-dim mt-0.5">
                        <span className="text-bm-cyan">{b.archetype}</span>
                        <span>· status: {b.status?.replaceAll("_", " ")}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
